import React, { useEffect, useState, useRef, useCallback } from "react";
import styles from "./style.module.scss";
import baseStyles from "../../style.module.scss";
import TextInput from "../../../../../../components/form/text-input";
import { Select } from "../../../../../../components/form/select";
import Switch from "../../../../../../components/form/switch";
import NumberInput from "../../../../../../components/form/number-input";
import InfoIcon from "../../../../../../assets/svg/icons/common/info";
import useSalesFormStore, {
  fetchMccLowRisk,
} from "../../../../../../stores/sales-form";
import { US_STATES, PaylianceIndustries } from "../../../../../../../constants";
import { useEMSValidation } from "../../../../../../hooks/useEMSValidation";
import AddressAutocomplete from "../../../../../../components/form/address-autocomplete";
import { googlePlaceToFormData } from "../../../../../../utils/address-parser";
import ReactTooltip from "react-tooltip";
import { useFormikContext } from "formik";
import Spinner from "../../../../../../components/Loaders/Spinner";

// Custom wrapper for Switch to better isolate click events
const SwitchWrapper = ({ id, name, label, checked, onChange }) => {
  return (
    <div
      className={styles.switchWrapper}
      style={{ width: "auto", display: "inline-flex" }}
    >
      <div style={{ width: "auto", display: "inline-flex" }}>
        <Switch
          id={id}
          name={name}
          label={label}
          checked={checked}
          onChange={onChange}
        />
      </div>
    </div>
  );
};

// Define options for dropdowns
const businessTypeOptions = [
  { value: "Sole Proprietor", label: "Sole Proprietor" },
  { value: "Partnership", label: "Partnership" },
  { value: "Corporation", label: "Corporation" },
  { value: "LLC", label: "LLC (single or multi-member)" },
];

const premiseTypeOptions = [
  { value: 1, label: "Commercial/Industrial" },
  { value: 2, label: "Kiosk" },
  { value: 3, label: "Mailbox" },
  { value: 4, label: "Office Building" },
  { value: 5, label: "Residential/Home Office" },
  { value: 6, label: "Retail Store Front" },
  { value: 7, label: "Mobile/On-the-Go" },
  { value: 8, label: "Website" },
];

// tooltip contents
const ToolTipLegalBusinessName =
  "This should be the legal name of your business as registered with the state. If applying as a sole proprietor, use your First and Last name.";
const ToolTipDBAStreet =
  "Enter the legal business address that you have registered with the state. This address should match your IRS form SS-4.";
const ToolTipBusinessStreet =
  "This address is where you're physically doing business (i.e. home address). This cannot be a PO Box.";
const ToolTipEIN =
  "You can find this number on your IRS Form-SS4. If you are a sole proprietor and haven't pulled an EIN/TAX ID you can use your SSN.";

/**
 * BusinessInformation Component
 * Renders a form for collecting business information including ownership, contact details, and addresses
 *
 * @param {Object} props - Component props
 * @param {Object} props.formik - Formik form instance containing values, errors, and handlers
 */
const BusinessInformation = () => {
  const {
    values,
    touched,
    errors,
    handleChange,
    handleBlur,
    setFieldValue,
    setFieldError,
    setFieldTouched,
  } = useFormikContext();
  const { validatePhone, validationStatuses, validatingPhone } =
    useEMSValidation();
  const mountedRef = useRef(false);
  const debounceTimeoutRef = useRef(null);

  const applicationData = useSalesFormStore((state) => state.applicationData);
  const hasPaylianceProcessor =
    applicationData?.override_bank?.some(
      (bank) => bank.label === "Payliance",
    ) ?? false;

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  const debouncedValidatePhone = useCallback(
    (value, name) => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      debounceTimeoutRef.current = setTimeout(async () => {
        if (!mountedRef.current) return;
        if (validatingPhone?.[name]) return;

        await validatePhone(value, name, setFieldError, setFieldTouched);
      }, 500);
    },
    [validatingPhone, validatePhone, setFieldError, setFieldTouched],
  );

  const handlePhoneChange = async (event) => {
    handleChange(event);
    if (!mountedRef.current) return;

    const { name, value } = event.target;

    if (validatingPhone[name]) return;

    debouncedValidatePhone(value, name);
  };

  const handlePhoneBlur = async (event) => {
    handleBlur(event);
    if (!mountedRef.current) {
      return;
    }

    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
      debounceTimeoutRef.current = null;
    }

    const { name, value } = event.target;
    if (validatingPhone[name]) return;

    await validatePhone(value, name, setFieldError, setFieldTouched);
  };

  const mccOptions = useSalesFormStore((state) => state.mccOptions);
  const [stateList, setStateList] = useState([]);

  /**
   * Initialize state list options on component mount
   */
  useEffect(() => {
    const stateOptions = Object.entries(US_STATES).map(([key, value]) => ({
      label: value,
      value: value,
    }));
    setStateList(stateOptions);
  }, []);

  /**
   * Handles the "Same as DBA" toggle functionality
   * Copies or clears legal address fields based on toggle state
   * @param {boolean} checked - Whether the toggle is checked
   */
  const handleSameAsDBA = (checked) => {
    setFieldValue("same_as_dba", checked ? "yes" : "no");
    if (checked) {
      let legalDBAFieldMap = {
        legal_address: "dba_address",
        legal_city: "dba_city",
        legal_state: "dba_state",
        legal_zip: "dba_zip",
        legal_phone: "business_phone",
      };

      Object.entries(legalDBAFieldMap).forEach(([key, value]) => {
        setFieldValue(key, values[value]);
        setFieldError(key, null);
      });

      // do this on the next loop to ensure fields don't error
      setTimeout(() => {
        Object.entries(legalDBAFieldMap).forEach(([field, value]) => {
          setFieldTouched(field, true, true);
        });
      }, 0);
    }
  };

  const handleDbaAddressSelected = (place) => {
    // Parse the Google place data
    const addressData = googlePlaceToFormData(place, {
      streetAddress: "dba_address",
      city: "dba_city",
      state: "dba_state",
      zipCode: "dba_zip",
    });

    // Special handling for state to ensure it matches the dropdown options
    if (addressData.dba_state) {
      // Find matching state in the state list options
      const matchingState = stateList.find(
        (option) =>
          option.label === addressData.dba_state ||
          option.value === addressData.dba_state,
      );

      if (matchingState) {
        addressData.dba_state = matchingState.value;
      } else {
        console.log(
          "No matching state found in dropdown for:",
          addressData.dba_state,
        );
      }
    }

    // Update the form fields
    Object.entries(addressData).forEach(([field, value]) => {
      console.log(`Setting ${field} to ${value}`);
      setFieldValue(field, value);
    });
  };

  const handleLegalAddressSelected = (place) => {
    // Parse the Google place data
    const addressData = googlePlaceToFormData(place, {
      streetAddress: "legal_address",
      city: "legal_city",
      state: "legal_state",
      zipCode: "legal_zip",
    });

    // Special handling for state to ensure it matches the dropdown options
    if (addressData.legal_state) {
      // Find matching state in the state list options
      const matchingState = stateList.find(
        (option) =>
          option.label === addressData.legal_state ||
          option.value === addressData.legal_state,
      );

      if (matchingState) {
        addressData.legal_state = matchingState.value;
      } else {
        console.log(
          "No matching state found in dropdown for:",
          addressData.legal_state,
        );
      }
    }

    // Update the form fields
    Object.entries(addressData).forEach(([field, value]) => {
      setFieldValue(field, value);
    });
  };

  return (
    <div className={baseStyles.sectionContainer}>
      <ReactTooltip className={styles.tooltip} />

      <div className={baseStyles.sectionHeader}>
        <h4 className={baseStyles.sectionTitle}>Business Information</h4>
      </div>

      <div className={baseStyles.sectionFormContent}>
        {/* Business Information Form */}
        <div className={styles.formGrid}>
          {/* Type of Ownership */}
          <div className={styles.formGroup}>
            <Select
              label="Type of Ownership"
              placeholder="Select type"
              id="type_of_ownership"
              name="type_of_ownership"
              options={businessTypeOptions}
              value={values.type_of_ownership}
              onBlur={handleBlur}
              onChange={(value) => {
                setFieldValue("type_of_ownership", value);
              }}
              touched={touched.type_of_ownership}
              errored={errors.type_of_ownership}
              error={errors.type_of_ownership}
              placeholderAsBlankOption={true}
            />
          </div>

          {/* Legal Business Name */}
          <div className={styles.formGroup}>
            <TextInput
              label="Legal Business Name"
              placeholder="Legal Business Name"
              type="text"
              id="legal_business_name"
              name="legal_business_name"
              value={values.legal_business_name}
              onChange={handleChange}
              onBlur={handleBlur}
              touched={touched.legal_business_name}
              errored={errors.legal_business_name}
              error={errors.legal_business_name}
              suffix={
                <InfoIcon
                  data-tip={ToolTipLegalBusinessName}
                  data-place="top"
                />
              }
            />
          </div>

          {/* DBA Name */}
          <div className={styles.formGroup}>
            <TextInput
              label={
                <>
                  DBA Name <span className={styles.optional}>(optional)</span>
                </>
              }
              placeholder="DBA Name (if different from legal)"
              type="text"
              id="dba_name"
              name="dba_name"
              value={values.dba_name}
              onChange={handleChange}
              onBlur={handleBlur}
              touched={touched.dba_name}
              errored={errors.dba_name}
              error={errors.dba_name}
            />
          </div>

          {/* Tax ID / EIN */}
          <div className={styles.formGroup}>
            <TextInput
              label="Tax ID / EIN"
              placeholder="Tax ID / EIN"
              type="text"
              id="tax_ein"
              name="tax_ein"
              defaultValue={values.tax_ein}
              onBlur={handleBlur}
              onChange={handleChange}
              touched={touched.tax_ein}
              errored={errors.tax_ein}
              error={errors.tax_ein}
              suffix={<InfoIcon data-tip={ToolTipEIN} data-place="top" />}
              cleaveOptions={{
                //No hyphen in a Canadian Business Number or EIN
                blocks: [2, 7],
                delimiter: "-",
                numericOnly: true,
                onValueChanged: (e) => {
                  setFieldValue("tax_ein", e.target.rawValue);
                },
              }}
            />
          </div>

          {/* Premise Type */}
          <div className={styles.formGroup}>
            <Select
              label="Premise Type"
              placeholder="Select Premise Type"
              id="premise_type"
              name="premise_type"
              options={premiseTypeOptions}
              value={values.premise_type}
              onBlur={handleBlur}
              onChange={(value) => {
                setFieldValue("premise_type", value);
              }}
              touched={touched.premise_type}
              errored={errors.premise_type}
              error={errors.premise_type}
            />
          </div>

          {/* Years and Months of ownership */}
          <div className={styles.formGroup}>
            <div className={styles.inputsRow}>
              <div className={styles.halfWidth}>
                <NumberInput
                  label="Years of ownership"
                  placeholder="0"
                  type="text"
                  id="ownership_years"
                  name="ownership_years"
                  min={0}
                  max={99}
                  value={values.ownership_years}
                  onBlur={handleBlur}
                  onChange={(value) => setFieldValue("ownership_years", value)}
                  suffix="Years"
                  error={errors.ownership_years}
                  touched={touched.ownership_years}
                  errored={errors.ownership_years}
                />
              </div>
              <div className={styles.halfWidth}>
                <NumberInput
                  label="Months of ownership"
                  placeholder="0"
                  type="text"
                  id="ownership_months"
                  name="ownership_months"
                  min={0}
                  max={11}
                  value={values.ownership_months}
                  onBlur={handleBlur}
                  onChange={(value) => setFieldValue("ownership_months", value)}
                  suffix="Months"
                  error={errors.ownership_months}
                  touched={touched.ownership_months}
                  errored={errors.ownership_months}
                />
              </div>
            </div>
          </div>

          {/* Business Type and Tax exempt on the same row */}
          <div className={styles.formGroup}>
            <Select
              label="Business Type"
              placeholder="Select Business Type"
              id="business_type"
              name="business_type"
              async
              search
              onBlur={handleBlur}
              onSearchChange={(searchTerm) => {
                fetchMccLowRisk(searchTerm);
              }}
              options={mccOptions}
              prevSelected={
                values.business_type_label
                  ? {
                    value: values.business_type,
                    label: values.business_type_label,
                  }
                  : null
              }
              value={values.business_type}
              onChange={(value) => {
                setFieldValue(
                  "business_type",
                  typeof value === "object" ? value.value : value,
                );
              }}
              touched={touched.business_type}
              errored={errors.business_type}
              error={errors.business_type}
            />
          </div>

          {hasPaylianceProcessor && (
            <div className={styles.formGroup}>
              <Select
                label="Payliance Industry"
                placeholder="Select Payliance Industry"
                id="payliance_industry"
                name="payliance_industry"
                search
                onBlur={handleBlur}
                options={PaylianceIndustries}
                value={values.payliance_industry}
                onChange={(value) => {
                  setFieldValue(
                    "payliance_industry",
                    typeof value === "object" ? value.value : value,
                  );
                }}
                touched={touched.payliance_industry}
                errored={errors.payliance_industry}
                error={errors.payliance_industry}
              />
            </div>
          )}

          {/* Tax exempt - with spacer div to prevent row-wide clicks */}
          <div className={styles.formGroup} style={{ display: "flex" }}>
            <div className={styles.switchItem}>
              <SwitchWrapper
                id="tax_exempt"
                name="tax_exempt"
                label="Tax exempt"
                checked={values.tax_exempt == "yes" ? true : false}
                onBlur={handleBlur}
                onChange={({ target }) =>
                  setFieldValue("tax_exempt", target.checked ? "yes" : "no")
                }
              />
            </div>
            <div className={styles.clickBlocker}></div>
          </div>
        </div>

        {/* CONTACTS */}
        <div className={styles.sectionDivider}>
          <h5 className={styles.sectionLabel}>CONTACTS</h5>
          <div className={styles.dividerLine}></div>
        </div>

        <div className={`${styles.formGrid} ${styles.contactsGrid}`}>
          {/* Business Phone Number */}
          <div className={styles.formGroup}>
            <TextInput
              label="Business Phone Number"
              placeholder="Business Phone Number"
              type="tel"
              id="business_phone"
              name="business_phone"
              value={values.business_phone}
              onBlur={(e) => handlePhoneBlur(e)}
              suffix={
                validatingPhone?.business_phone ? <Spinner size={16} /> : null
              }
              touched={touched.business_phone}
              errored={
                errors.business_phone ||
                validationStatuses?.business_phone === false
              }
              error={
                errors.business_phone ||
                validationStatuses?.business_phone === false
                  ? "* Enter a valid phone number"
                  : null
              }
              cleaveOptions={{
                blocks: [3, 3, 4],
                delimiters: ["-", "-"],
                numericOnly: true,
                onValueChanged: (e) => {
                  handlePhoneChange(e);
                },
              }}
            />
          </div>

          {/* Business Email */}
          <div className={styles.formGroup}>
            <TextInput
              label="Business Email"
              placeholder="Business Email"
              type="email"
              id="business_email"
              name="business_email"
              value={values.business_email}
              onChange={handleChange}
              onBlur={handleBlur}
              touched={touched.business_email}
              errored={errors.business_email}
              error={errors.business_email}
            />
          </div>

          {/* Business Website */}
          <div className={styles.formGroup}>
            <TextInput
              label={
                <>
                  Business Website URL{" "}
                  <span className={styles.optional}>(optional)</span>
                </>
              }
              placeholder="https://"
              type="text"
              id="business_website"
              name="business_website"
              value={values.business_website}
              onChange={handleChange}
              onBlur={handleBlur}
              touched={touched.business_website}
              errored={errors.business_website}
              error={errors.business_website}
            />
          </div>
        </div>

        {/* DBA ADDRESS */}
        <div className={styles.sectionDivider}>
          <h5 className={styles.sectionLabel}>DBA ADDRESS</h5>
          <div className={styles.dividerLine}></div>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          <div className={styles.formGroupFullWidth}>
            <AddressAutocomplete
              label="DBA Business Address"
              placeholder="DBA Business Address"
              id="dba_address"
              name="dba_address"
              value={values.dba_address}
              onChange={handleChange}
              onPlaceSelected={handleDbaAddressSelected}
              onBlur={handleBlur}
              touched={touched.dba_address}
              errored={errors.dba_address}
              error={errors.dba_address}
            />
          </div>

          <div className={styles.addressGrid}>
            <TextInput
              label="DBA City"
              placeholder="City"
              type="text"
              id="dba_city"
              name="dba_city"
              value={values.dba_city}
              onChange={handleChange}
              onBlur={handleBlur}
              touched={touched.dba_city}
              errored={errors.dba_city}
              error={errors.dba_city}
            />

            <Select
              label="DBA State"
              placeholder="Select state"
              id="dba_state"
              name="dba_state"
              options={stateList}
              value={values.dba_state}
              onBlur={handleBlur}
              onChange={(value) => {
                setFieldValue("dba_state", value);
              }}
              touched={touched.dba_state}
              errored={errors.dba_state}
              error={errors.dba_state}
            />

            <TextInput
              label="DBA Zip"
              placeholder="Zip"
              type="text"
              id="dba_zip"
              name="dba_zip"
              value={values.dba_zip}
              onChange={handleChange}
              onBlur={handleBlur}
              touched={touched.dba_zip}
              errored={errors.dba_zip}
              error={errors.dba_zip}
              suffix={<InfoIcon />}
              cleaveOptions={{
                blocks: [5],
                delimiter: "",
                numericOnly: true,
                onValueChanged: (e) => {
                  handleChange(e);
                },
              }}
            />

            <NumberInput
              label="Years at Location"
              id="location_years"
              name="location_years"
              min={0}
              max={99}
              value={values.location_years}
              onBlur={handleBlur}
              onChange={(value) => setFieldValue("location_years", value)}
              suffix="Years"
              error={errors.location_years}
              touched={touched.location_years}
              errored={errors.location_years}
            />

            <NumberInput
              label="Months at Location"
              id="location_months"
              name="location_months"
              min={0}
              max={11}
              value={values.location_months}
              onBlur={handleBlur}
              onChange={(value) => setFieldValue("location_months", value)}
              suffix="Months"
              error={errors.location_months}
              touched={touched.location_months}
              errored={errors.location_months}
            />
          </div>
        </div>

        {/* LEGAL BUSINESS ADDRESS */}
        <div className={styles.sectionDivider}>
          <h5 className={styles.sectionLabel}>LEGAL BUSINESS ADDRESS</h5>
          <div className={styles.dividerLine}></div>
        </div>

        <div className={styles.formGrid}>
          <div className={styles.formGroup}>
            <Switch
              id="same_as_dba"
              name="same_as_dba"
              label="Same as DBA"
              checked={values.same_as_dba === "yes"}
              onChange={({ target }) => handleSameAsDBA(target.checked)}
            />
            <div className={styles.clickBlocker}></div>
          </div>
          <div className={styles.formGroup}></div>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          <div className={styles.formGroupFullWidth}>
            <AddressAutocomplete
              label="Legal Business Address"
              placeholder="Legal Business Address"
              id="legal_address"
              name="legal_address"
              value={values.legal_address}
              onChange={handleChange}
              disabled={values.same_as_dba === "yes"}
              onPlaceSelected={handleLegalAddressSelected}
              onBlur={handleBlur}
              touched={touched.legal_address}
              errored={errors.legal_address}
              error={errors.legal_address}
            />
          </div>

          <div className={styles.addressGrid}>
            <TextInput
              label="Legal City"
              placeholder="City"
              type="text"
              id="legal_city"
              name="legal_city"
              value={values.legal_city}
              onChange={handleChange}
              onBlur={handleBlur}
              touched={touched.legal_city}
              errored={errors.legal_city}
              error={errors.legal_city}
              disabled={values.same_as_dba === "yes"}
            />

            <Select
              label="Legal State"
              placeholder="Select state"
              id="legal_state"
              name="legal_state"
              options={stateList}
              value={values.legal_state}
              onChange={(value) => {
                setFieldValue("legal_state", value);
              }}
              onBlur={handleBlur}
              touched={touched.legal_state}
              errored={errors.legal_state}
              error={errors.legal_state}
              disabled={values.same_as_dba === "yes"}
            />

            <TextInput
              label="Legal Zip"
              placeholder="Zip"
              type="text"
              id="legal_zip"
              name="legal_zip"
              value={values.legal_zip}
              onChange={handleChange}
              onBlur={handleBlur}
              touched={touched.legal_zip}
              errored={errors.legal_zip}
              error={errors.legal_zip}
              disabled={values.same_as_dba === "yes"}
              cleaveOptions={{
                blocks: [5],
                delimiter: "",
                numericOnly: true,
                onValueChanged: (e) => {
                  setFieldValue("legal_zip", e.target.value);
                },
              }}
            />
          </div>

          <div className={styles.addressGrid}>
            <TextInput
              label="Legal Phone Number"
              placeholder="Legal Phone Number"
              type="tel"
              id="legal_phone"
              name="legal_phone"
              value={values.legal_phone}
              onBlur={(e) => handlePhoneBlur(e)}
              touched={touched.legal_phone}
              errored={
                errors.legal_phone || validationStatuses?.legal_phone === false
              }
              error={
                errors.legal_phone || validationStatuses?.legal_phone === false
                  ? "* Enter a valid phone number"
                  : null
              }
              suffix={
                validatingPhone?.legal_phone ? <Spinner size={16} /> : null
              }
              disabled={values.same_as_dba === "yes"}
              cleaveOptions={{
                blocks: [3, 3, 4],
                delimiters: ["-", "-"],
                numericOnly: true,
                onValueChanged: (e) => {
                  handlePhoneChange(e);
                },
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default BusinessInformation;
